import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

interface ImageCarouselProps {
  images: string[];
  alt: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, alt }) => {
  const safeImages = useMemo(() => {
    const validImages = (images ?? [])
      .map((image) => image.trim())
      .filter((image): image is string => image.length > 0);

    return validImages.length > 0 ? validImages : ["/placeholder.png"];
  }, [images]);
  const [index, setIndex] = useState(0);
  const currentImage = safeImages[index] ?? safeImages[0];

  useEffect(() => {
    setIndex((currentIndex) => Math.min(currentIndex, safeImages.length - 1));
  }, [safeImages.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + safeImages.length) % safeImages.length);
  }, [safeImages.length]);

  const goNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % safeImages.length);
  }, [safeImages.length]);

  return (
    <Container>
      <Viewport>
        <SlideImage key={`${currentImage}-${index}`} src={currentImage} alt={alt} />
        {safeImages.length > 1 && (
          <>
            <NavButton aria-label="이전 이미지" onClick={goPrev} position="left">‹</NavButton>
            <NavButton aria-label="다음 이미지" onClick={goNext} position="right">›</NavButton>
            <Dots>
              {safeImages.map((_, i) => (
                <Dot key={i} active={i === index} onClick={() => setIndex(i)} />
              ))}
            </Dots>
          </>
        )}
      </Viewport>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  aspect-ratio: 1.21;
`;

const Viewport = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 4px;
`;

const SlideImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

const NavButton = styled.button<{ position: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.position === "left" ? "left: 8px;" : "right: 8px;")}
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  cursor: pointer;
  font-size: 20px;
`;

const Dots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  display: flex;
  gap: 6px;
  justify-content: center;
`;

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${(p) => (p.active ? "#27c360" : "rgba(255,255,255,0.7)")};
  cursor: pointer;
`;

export default ImageCarousel;

